
export default function WhySpeedMatters() {
  const reasons = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Accumulating Carrying Costs',
      description:
        'Every month of delay costs $9,500+ in mortgage, taxes, insurance, and maintenance. A 6-month delay equals $57,000 in unnecessary expenses.',
      urgency: 'High Financial Impact',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Market Timing Risk',
      description:
        'Real estate markets fluctuate. Interest rate increases or economic downturns can reduce property values by 10-20% in months.',
      urgency: 'Market Volatility',
    },
    {
      icon: 'ri-team-line',
      title: 'Family Relationship Strain',
      description:
        'Extended property ownership creates ongoing disagreements about management, expenses, and strategy. Quick resolution preserves family harmony.',
      urgency: 'Emotional Toll',
    },
    {
      icon: 'ri-file-damage-line',
      title: 'Property Deterioration',
      description:
        'Vacant or poorly managed properties decline rapidly. Deferred maintenance, vandalism, and weather damage compound monthly.',
      urgency: 'Physical Depreciation',
    },
    {
      icon: 'ri-scales-3-line',
      title: 'Legal Complications',
      description:
        'The longer probate drags on, the more legal fees accumulate. Additional heirs, creditors, or title issues may emerge over time.',
      urgency: 'Increasing Complexity',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Opportunity Cost',
      // Escape the apostrophe to avoid breaking the string literal
      description:
        'Capital tied up in an inherited property can\'t be invested elsewhere. Missing 12 months of 8% returns on $500,000 costs $40,000.',
      urgency: 'Lost Investment Returns',
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Why Speed Matters When Selling Inherited Apartments
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Time is not on your side with inherited properties. Every month of delay creates additional costs, risks, and
            complications.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {reasons.map((item, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-teal-500"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-lg mb-6">
                <i className={`${item.icon} text-3xl`}></i>
              </div>
              <div className="mb-4">
                <span className="inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                  {item.urgency}
                </span>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-10 text-white mb-12">
          <h3 className="text-3xl font-bold mb-8 text-center">The Compounding Cost of Delay</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-2xl font-bold mb-4 text-teal-400">Traditional Sale Timeline</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-white/80">Probate process</span>
                  <span className="font-bold">12 months</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-white/80">List with agent</span>
                  <span className="font-bold">2 months</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-white/80">Buyer financing</span>
                  <span className="font-bold">2 months</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-white/80">Repairs/contingencies</span>
                  <span className="font-bold">1 month</span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="text-xl font-bold">Total Timeline:</span>
                  <span className="text-2xl font-bold text-red-400">17 months</span>
                </div>
                <div className="flex justify-between items-center pt-2 text-red-400">
                  <span className="text-lg font-bold">Total Carrying Costs:</span>
                  <span className="text-2xl font-bold">$161,500</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-2xl font-bold mb-4 text-teal-400">Our Fast-Track Process</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-white/80">Probate process</span>
                  <span className="font-bold">12 months</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-white/80">Cash offer (immediate)</span>
                  <span className="font-bold">1 day</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-white/80">Close after approval</span>
                  <span className="font-bold">2-4 weeks</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-white/80">Repairs needed</span>
                  <span className="font-bold">None</span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="text-xl font-bold">Total Timeline:</span>
                  <span className="text-2xl font-bold text-teal-400">12.5 months</span>
                </div>
                <div className="flex justify-between items-center pt-2 text-teal-400">
                  <span className="text-lg font-bold">Total Carrying Costs:</span>
                  <span className="text-2xl font-bold">$118,750</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/20 text-center">
            <p className="text-2xl font-bold text-teal-400">You Save: $42,750 + Reduced Stress</p>
            <p className="text-white/80 mt-2">
              Plus faster access to capital for investment or personal needs
            </p>
          </div>
        </div>

        <div className="relative">
          <img
            src="https://public.readdy.ai/ai/img_res/35ed4ff1a609eac8a9f1b6e3c8859016.jpg"
            alt="fast inherited apartment building sale process"
            className="w-full h-96 rounded-2xl shadow-2xl object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent rounded-2xl flex items-center">
            <div className="px-12 max-w-2xl">
              <h3 className="text-4xl font-bold text-white mb-4">Time Is Money—Literally</h3>
              <p className="text-xl text-white/90 leading-relaxed">
                Every month you wait costs thousands in carrying expenses and lost opportunity. Our streamlined process
                gets you to closing faster, preserving your equity and peace of mind.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
